// frontend/src/components/equipos/ModalMapaGPSEquipo.jsx
// Modal que muestra la posición GPS actual de un equipo específico.
// Usa marcador nativo imperativo (new google.maps.Marker) para evitar
// el race condition del wrapper JSX al abrir el modal rápidamente.
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { useJsApiLoader, GoogleMap } from '@react-google-maps/api';
import gpsService from '../../services/gpsService';
import uiPreferencesService from '../../services/uiPreferencesService';
import { GOOGLE_MAPS_LIBRARIES } from '../../services/googleMapsConfig';
import useBackdropCloseGuard from '../../hooks/useBackdropCloseGuard';
import { crearPopupOverlay } from '../../services/googleMapsPopup';
import { ZONA_ARGENTINA } from '../../utils/dateUtils';

const UI_PREF_SCOPE = 'mapa_estilo_gps';
const POLL_MS = 60_000;
const MAP_CONTAINER = { width: '100%', height: '100%' };

// Paleta "Night Mode" de Google Maps leída desde theme.mapStyle (ver theme.js)
function buildDarkStyles(m) {
  return [
    { elementType: 'geometry',            stylers: [{ color: m.darkGeometry }] },
    { elementType: 'labels.icon',         stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill',    stylers: [{ color: m.darkLabelFill }] },
    { elementType: 'labels.text.stroke',  stylers: [{ color: m.darkLabelStroke }] },
    { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: m.darkAdminLocalityLabelFill }] },
    { featureType: 'poi.park',     elementType: 'geometry',        stylers: [{ color: m.darkPoiParkGeometry }] },
    { featureType: 'road',         elementType: 'geometry.fill',   stylers: [{ color: m.darkRoadGeometryFill }] },
    { featureType: 'road',         elementType: 'labels.text.fill',stylers: [{ color: m.darkRoadLabelFill }] },
    { featureType: 'road.arterial',elementType: 'geometry',        stylers: [{ color: m.darkRoadArterialGeometry }] },
    { featureType: 'road.highway', elementType: 'geometry',        stylers: [{ color: m.darkRoadHighwayGeometry }] },
    { featureType: 'water',        elementType: 'geometry',        stylers: [{ color: m.darkWaterGeometry }] },
  ];
}

// ─── Styled ────────────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 1100;
  background: ${({ theme }) => theme.colors.overlayBackdrop};
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
`;

const ModalBox = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  box-shadow: ${({ theme }) => theme.colors.shadowModal};
  width: 100%; max-width: 820px;
  display: flex; flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
  outline: none;
`;

const ModalHeader = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 14px 18px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderDefault};
  flex-shrink: 0;
`;

const ModalTitle = styled.h3`
  margin: 0; flex: 1;
  font-size: 15px; font-weight: 600;
  color: ${({ theme }) => theme.colors.txtPrincipal};
`;

const CloseBtn = styled.button`
  background: none; border: none; cursor: pointer;
  font-size: 18px; line-height: 1; padding: 2px 6px;
  color: ${({ theme }) => theme.colors.txtSecondary};
  border-radius: 4px;
  &:hover { background: ${({ theme }) => theme.colors.surfaceHover}; color: ${({ theme }) => theme.colors.danger}; }
`;

const MapWrapper = styled.div`
  position: relative;
  height: 460px;
  flex-shrink: 0;
  zoom: calc(1 / var(--app-zoom, 1));
`;

const MapControls = styled.div`
  position: absolute; top: 10px; right: 10px; z-index: 10;
  display: flex; flex-direction: column; gap: 6px;
`;

const MapBtn = styled.button`
  background: ${({ theme, $active }) => $active ? theme.colors.info : theme.colors.surface};
  color: ${({ theme, $active }) => $active ? theme.colors.onPrimary : theme.colors.txtPrincipal};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  border-radius: 6px; padding: 5px 10px; font-size: 0.75rem;
  cursor: pointer; display: flex; align-items: center; gap: 5px; white-space: nowrap;
  box-shadow: 0 1px 4px ${({ theme }) => theme.colors.shadowSoft};
  &:hover { background: ${({ theme, $active }) => $active ? theme.colors.infoHover : theme.colors.surfaceHover}; }
`;

const StatusBar = styled.div`
  padding: 8px 14px;
  border-top: 1px solid ${({ theme }) => theme.colors.borderDefault};
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  font-size: 11px; color: ${({ theme }) => theme.colors.txtSecondary};
  flex-shrink: 0;
`;

const Dot = styled.span`
  display: inline-block; width: 8px; height: 8px; border-radius: 50%;
  background: ${({ $color }) => $color}; flex-shrink: 0;
`;

const LoadingBox = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  height: 100%; gap: 8px; padding: 24px;
  color: ${({ theme }) => theme.colors.txtSecondary}; font-size: 13px; text-align: center;
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function frescuraColor(timestampStr, theme) {
  if (!timestampStr) return theme.colors.txtSecondary;
  const mins = (Date.now() - new Date(timestampStr).getTime()) / 60000;
  if (mins < 15) return theme.colors.success;
  if (mins < 60) return theme.colors.warning;
  return theme.colors.danger;
}

function formatTimestamp(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('es-AR', { timeZone: ZONA_ARGENTINA, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function formatAntiguedad(timestampStr) {
  if (!timestampStr) return '—';
  const mins = Math.floor((Date.now() - new Date(timestampStr).getTime()) / 60000);
  if (mins < 1) return 'En tiempo real';
  if (mins < 60) return `Hace ${mins} min`;
  return `Hace ${Math.floor(mins / 60)}h ${mins % 60}min`;
}

function buildPopupHtml(nombre, pos, theme) {
  const c = theme.colors;
  const colorFrescura = frescuraColor(pos.timestamp_gps, theme);
  const motorColor = pos.motor_encendido ? c.success : c.danger;
  const vel = pos.velocidad != null
    ? `<div style="display:flex;align-items:center;gap:5px;margin:2px 0;color:${c.txtSecondary}">
         <span>⚡</span><span>${pos.velocidad} km/h</span>
       </div>`
    : '';
  const motor = pos.motor_encendido != null
    ? `<div style="display:flex;align-items:center;gap:5px;margin:2px 0;color:${c.txtSecondary}">
         <span style="width:8px;height:8px;border-radius:50%;background:${motorColor};display:inline-block;flex-shrink:0"></span>
         <span>Motor ${pos.motor_encendido ? 'encendido' : 'apagado'}</span>
       </div>`
    : '';
  return `<div style="
    font-family:'Segoe UI',sans-serif;
    font-size:12px;
    background:${c.surface};
    color:${c.txtPrincipal};
    border-radius:8px;
    padding:10px 12px;
    min-width:175px;
    line-height:1.5;
    margin:0;
  ">
    <div style="font-size:13px;font-weight:600;margin-bottom:5px">${nombre}</div>
    ${vel}
    ${motor}
    <div style="display:flex;align-items:center;gap:5px;margin:2px 0;color:${c.txtSecondary}">
      <span>🕐</span><span>${formatTimestamp(pos.timestamp_gps)}</span>
    </div>
    <div style="margin-top:3px;font-size:11px;color:${colorFrescura}">${formatAntiguedad(pos.timestamp_gps)}</div>
  </div>`;
}

function aplicarEstilo(map, estilo, mapStyleTokens) {
  if (!map) return;
  if (estilo === 'satellite') {
    map.setMapTypeId('hybrid');
    map.setOptions({ styles: [] });
  } else if (estilo === 'dark') {
    map.setMapTypeId('roadmap');
    map.setOptions({ styles: buildDarkStyles(mapStyleTokens) });
  } else {
    map.setMapTypeId('roadmap');
    map.setOptions({ styles: [] });
  }
}

// ─── Componente ───────────────────────────────────────────────────────────────

export default function ModalMapaGPSEquipo({ equipoId, equipoNombre, onClose }) {
  const theme = useTheme();

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const hasKey = apiKey && apiKey !== 'TU_CLAVE_AQUI';

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [ubicacion, setUbicacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapaEstilo, setMapaEstilo] = useState('roadmap');
  const [prefCargada, setPrefCargada] = useState(false);
  const mapRef = useRef(null);
  const markerRef = useRef(null);    // google.maps.Marker nativo
  const popupRef = useRef(null);     // PopupOverlay nativo (sin burbuja de InfoWindow)
  const ubicacionRef = useRef(null); // espejo síncrono para onLoad
  const backdropGuard = useBackdropCloseGuard();
  const centerDefault = useMemo(() => ({ lat: -34.6, lng: -63.0 }), []);

  // Cargar preferencia de capa guardada
  useEffect(() => {
    uiPreferencesService.getScope(UI_PREF_SCOPE)
      .then(v => { if (v) setMapaEstilo(v); })
      .catch(() => {})
      .finally(() => setPrefCargada(true));
  }, []);

  // ── Colocar / mover marcador nativo + popup overlay ──────────────────────
  const sincronizarMarcador = useCallback((pos) => {
    const map = mapRef.current;
    if (!map || !window.google?.maps) return;

    if (!pos) {
      popupRef.current?.ocultar();
      if (markerRef.current) { markerRef.current.setMap(null); markerRef.current = null; }
      return;
    }

    const lat = parseFloat(pos.latitud);
    const lng = parseFloat(pos.longitud);
    if (isNaN(lat) || isNaN(lng)) return;

    const nombre = equipoNombre || `Equipo #${equipoId}`;
    const icon = {
      path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
      scale: 6,
      fillColor: frescuraColor(pos.timestamp_gps, theme),
      fillOpacity: 1,
      strokeWeight: 2,
      strokeColor: theme.colors.white,
    };

    if (!markerRef.current) {
      markerRef.current = new window.google.maps.Marker({
        map,
        position: { lat, lng },
        title: nombre,
        icon,
        zIndex: 10,
      });
    } else {
      markerRef.current.setPosition({ lat, lng });
      markerRef.current.setIcon(icon);
    }

    // Popup siempre visible sobre el marcador, se actualiza en cada poll
    popupRef.current?.mostrar(
      new window.google.maps.LatLng(lat, lng),
      buildPopupHtml(nombre, pos, theme)
    );

    map.panTo({ lat, lng });
    map.setZoom(14);
  }, [equipoId, equipoNombre, theme]);

  const fetchUbicacion = useCallback(async () => {
    try {
      const pos = await gpsService.ubicacionEquipo(equipoId);
      ubicacionRef.current = pos;
      setUbicacion(pos);
    } catch {
      ubicacionRef.current = null;
      setUbicacion(null);
    } finally {
      setLoading(false);
    }
  }, [equipoId]);

  useEffect(() => {
    fetchUbicacion();
    const interval = setInterval(fetchUbicacion, POLL_MS);
    return () => clearInterval(interval);
  }, [fetchUbicacion]);

  // Actualizar marcador cada vez que llega una nueva posición
  useEffect(() => {
    sincronizarMarcador(ubicacion);
  }, [ubicacion, sincronizarMarcador]);

  // Limpiar marcador y popup al desmontar
  useEffect(() => () => {
    popupRef.current?.destruir();
    if (markerRef.current) { markerRef.current.setMap(null); markerRef.current = null; }
  }, []);

  const cambiarEstilo = useCallback((estilo) => {
    setMapaEstilo(estilo);
    aplicarEstilo(mapRef.current, estilo, theme.mapStyle);
    uiPreferencesService.setScope(UI_PREF_SCOPE, estilo).catch(() => {});
  }, [theme.mapStyle]);

  const mapOptions = {
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: true,
    scrollwheel: true,
    gestureHandling: 'greedy',
  };

  const tienePos = ubicacion
    && !isNaN(parseFloat(ubicacion.latitud))
    && !isNaN(parseFloat(ubicacion.longitud));

  const colorFrescura = frescuraColor(ubicacion?.timestamp_gps, theme);

  return (
    <Overlay
      onMouseDown={backdropGuard.onBackdropMouseDown}
      onClick={backdropGuard.buildOnBackdropClick(onClose)}
    >
      <ModalBox onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <i className="fas fa-map-marker-alt" style={{ color: theme.colors.primary, fontSize: 15 }} />
          <ModalTitle>Ubicación GPS — {equipoNombre || `Equipo #${equipoId}`}</ModalTitle>
          <CloseBtn type="button" tabIndex={-1} onClick={onClose} title="Cerrar">×</CloseBtn>
        </ModalHeader>

        <MapWrapper>
          {!hasKey ? (
            <LoadingBox>
              <i className="fas fa-broadcast-tower" style={{ fontSize: 32 }} />
              <span>Configurá <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> en el archivo <code>.env</code> del frontend.</span>
            </LoadingBox>
          ) : loadError ? (
            <LoadingBox>Error al cargar Google Maps.</LoadingBox>
          ) : !isLoaded || !prefCargada ? (
            <LoadingBox>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: 24 }} />
              <span>Cargando mapa…</span>
            </LoadingBox>
          ) : (
            <>
              <GoogleMap
                mapContainerStyle={MAP_CONTAINER}
                zoom={5}
                center={centerDefault}
                options={mapOptions}
                onLoad={m => {
                  mapRef.current = m;
                  aplicarEstilo(m, mapaEstilo, theme.mapStyle);
                  const popup = crearPopupOverlay();
                  popup.setMap(m);
                  popupRef.current = popup;
                  // Si la posición ya llegó antes de que el mapa terminara de cargar,
                  // colocar el marcador ahora usando la ref síncrona
                  if (ubicacionRef.current) {
                    sincronizarMarcador(ubicacionRef.current);
                  }
                }}
              >
                <MapControls>
                  <MapBtn $active={mapaEstilo === 'roadmap'} onClick={() => cambiarEstilo('roadmap')}>
                    <i className="fas fa-map" /> Normal
                  </MapBtn>
                  <MapBtn $active={mapaEstilo === 'dark'} onClick={() => cambiarEstilo('dark')}>
                    <i className="fas fa-moon" /> Oscuro
                  </MapBtn>
                  <MapBtn $active={mapaEstilo === 'satellite'} onClick={() => cambiarEstilo('satellite')}>
                    <i className="fas fa-satellite" /> Satélite
                  </MapBtn>
                </MapControls>
              </GoogleMap>

              {/* Overlay de sin vinculación — superpuesto, no reemplaza el mapa */}
              {!loading && !tienePos && (
                <LoadingBox style={{ position: 'absolute', inset: 0, background: theme.colors.surface }}>
                  <i className="fas fa-satellite-dish" style={{ fontSize: 36, opacity: 0.5 }} />
                  <strong style={{ fontSize: 14 }}>Sin vinculación GPS</strong>
                  <span style={{ maxWidth: 340 }}>
                    Este equipo no tiene posición GPS registrada. Para verlo en el mapa,
                    configurá su vinculación en <strong>Módulo Logística → Configuración GPS</strong>.
                  </span>
                </LoadingBox>
              )}
            </>
          )}
        </MapWrapper>

        <StatusBar>
          {loading && <span><i className="fas fa-spinner fa-spin" /> Obteniendo posición…</span>}
          {!loading && tienePos && (
            <>
              <Dot $color={colorFrescura} />
              <span>{formatAntiguedad(ubicacion.timestamp_gps)}</span>
              <span style={{ color: theme.colors.borderDefault }}>|</span>
              <span>{formatTimestamp(ubicacion.timestamp_gps)}</span>
              {ubicacion.velocidad != null && (
                <>
                  <span style={{ color: theme.colors.borderDefault }}>|</span>
                  <span><i className="fas fa-tachometer-alt" /> {ubicacion.velocidad} km/h</span>
                </>
              )}
              {ubicacion.motor_encendido != null && (
                <>
                  <span style={{ color: theme.colors.borderDefault }}>|</span>
                  <Dot $color={ubicacion.motor_encendido ? theme.colors.success : theme.colors.danger} />
                  <span>Motor {ubicacion.motor_encendido ? 'encendido' : 'apagado'}</span>
                </>
              )}
            </>
          )}
          <span style={{ marginLeft: 'auto', cursor: 'pointer', color: theme.colors.info }}
            onClick={fetchUbicacion} title="Actualizar ahora">
            <i className="fas fa-sync-alt" /> Actualizar
          </span>
        </StatusBar>
      </ModalBox>
    </Overlay>
  );
}
